import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { cn } from "helpers/classNames";
import { SeparatorIcon } from "./icons/SeparatorIcon";
import { FacilityIcon } from "./icons/FacilityIcon";
import { CustomerIcon } from "./icons/CustomerIcon";
import { UppdragIcon } from "./icons/UppdragIcon";
import { Uppdrag } from "types/uppdrag.type";
import { UppdragTypeList } from "pages/app/uppdrag-list-page/UppdragTypeList";
import { Link } from "./Link";
import { ReactNode } from "react";
import { type LinkProps } from "@tanstack/react-router";

type BreadCrumb = {
  label: string;
  link?: Pick<LinkProps, "to" | "params" | "search">;
  iconType?: "separator" | "facility" | "customer" | "uppdrag";
};
type Props = {
  header: string | ReactNode;
  breadCrumbList?: BreadCrumb[];
  iconType?: "separator" | "facility" | "customer" | "uppdrag";
  // actionMenuItems?: ActionMenuItem[];
  // actionMenuText?: string;
  uppdrag?: Uppdrag;
  className?: string;
};

const headerIconClassName = "h-[16px] w-[16px] text-gray-600 mr-1";

export const PageHeader = ({
  header,
  breadCrumbList = [],
  iconType,
  className,
  // actionMenuItems,
  // actionMenuText,
  uppdrag,
}: Props) => (
  <div className={cn("mb-4 py-2", className)}>
    <BreadCrumbs breadCrumbList={breadCrumbList} />
    <div className="mt-3 md:mt-1 md:flex md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        <h2 className="flex items-center justify-between text-xl/7 font-bold text-gray-900 sm:truncate sm:text-xl sm:tracking-tight">
          <span className="flex items-center">
            {iconType === "separator" && (
              <SeparatorIcon className={headerIconClassName} />
            )}
            {iconType === "facility" && (
              <FacilityIcon className={headerIconClassName} />
            )}
            {iconType === "customer" && (
              <CustomerIcon className={headerIconClassName} />
            )}
            {iconType === "uppdrag" && (
              <UppdragIcon className={headerIconClassName} />
            )}
            {header}
            <span className="ml-2 flex items-center space-x-4">
              {!!uppdrag && <UppdragTypeList uppdrag={uppdrag} />}
            </span>
          </span>
        </h2>
      </div>
    </div>
  </div>
);

type BreadCrumbsProps = {
  breadCrumbList: BreadCrumb[];
};

const breadcrumbIconClassName = "h-[14px] w-[14px] text-gray-600 mr-0.5";

const BreadCrumbs = ({ breadCrumbList }: BreadCrumbsProps) => {
  if (breadCrumbList.length === 0) {
    return null;
  }
  return (
    <nav aria-label="Breadcrumb" className="flex">
      <div className="flex flex-col pl-0.5 sm:flex-row sm:items-center">
        {breadCrumbList.map(({ label, link, iconType }, index) => (
          <div key={label} className={cn(index > 0 && "py-1 sm:ml-2 sm:py-0")}>
            <div className="flex items-center">
              {index > 0 && (
                <ChevronRightIcon
                  aria-hidden="true"
                  className="hidden size-5 shrink-0 text-gray-500 sm:block"
                />
              )}
              {iconType === "separator" && (
                <SeparatorIcon className={breadcrumbIconClassName} />
              )}
              {iconType === "facility" && (
                <FacilityIcon className={breadcrumbIconClassName} />
              )}
              {iconType === "customer" && (
                <CustomerIcon className={breadcrumbIconClassName} />
              )}
              {iconType === "uppdrag" && (
                <UppdragIcon className={breadcrumbIconClassName} />
              )}
              {link ? (
                <Link {...link} className="text-sm" variant="gray">
                  {label}
                </Link>
              ) : (
                <span className="text-sm font-medium text-gray-800">
                  {label}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </nav>
  );
};
